/**
 * CTRL_08 Plugin - Human Oversight
 * Custom UI with human review tracking and approval workflows
 * Extends GenericConfigPanel with review queue dashboard
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Tag,
  Space,
  Progress,
} from 'antd';
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors } from '../../../../theme/designTokens';

/**
 * Human Oversight Dashboard Component
 */
function HumanOversightDashboard({ config, projectId }) {
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    avgReviewTime: 0,
  });
  const [reviewQueue, setReviewQueue] = useState([]);

  useEffect(() => {
    if (config?.enabled && projectId) {
      fetchReviewStats();
    }
  }, [config, projectId]);

  const fetchReviewStats = async () => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      setStats({
        pending: 12,
        approved: 458,
        rejected: 23,
        avgReviewTime: 4.5,
      });
      setReviewQueue([
        {
          id: 1,
          decision: 'Model deployment to production',
          requester: 'john.doe',
          priority: 'high',
          submitted: new Date(Date.now() - 7200000).toISOString(),
        },
        {
          id: 2,
          decision: 'Training data update',
          requester: 'jane.smith',
          priority: 'medium',
          submitted: new Date(Date.now() - 3600000).toISOString(),
        },
      ]);
    }, 500);
  };

  const columns = [
    {
      title: 'Decision',
      dataIndex: 'decision',
      key: 'decision',
    },
    {
      title: 'Requester',
      dataIndex: 'requester',
      key: 'requester',
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        const color = priority === 'high' ? 'red' : priority === 'medium' ? 'orange' : 'blue';
        return <Tag color={color}>{priority.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Waiting Time',
      dataIndex: 'submitted',
      key: 'waiting',
      render: (ts) => {
        const hours = Math.floor((Date.now() - new Date(ts)) / 3600000);
        return `${hours}h`;
      },
    },
  ];

  if (!config?.enabled) {
    return (
      <Alert
        message="Human Oversight Disabled"
        description="Enable human oversight to require manual approval for critical decisions."
        type="info"
        showIcon
      />
    );
  }

  const totalReviews = stats.approved + stats.rejected;
  const approvalRate = totalReviews > 0 ? ((stats.approved / totalReviews) * 100).toFixed(1) : 0;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Review Statistics */}
      <Row gutter={16}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Pending Reviews"
              value={stats.pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: colors.status.warning }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Approved"
              value={stats.approved}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: colors.status.pass }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Approval Rate"
              value={approvalRate}
              suffix="%"
              prefix={<CheckCircleOutlined />}
            />
            <Progress
              percent={approvalRate}
              strokeColor={colors.status.pass}
              size="small"
              showInfo={false}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Avg Review Time"
              value={stats.avgReviewTime}
              suffix="hours"
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Review Queue */}
      {reviewQueue.length > 0 && (
        <div>
          <h4>Pending Review Queue</h4>
          <Table
            columns={columns}
            dataSource={reviewQueue}
            rowKey="id"
            size="small"
            pagination={false}
          />
        </div>
      )}
    </Space>
  );
}

/**
 * CTRL_08 Plugin Component
 */
function CTRL_08_Plugin({ projectId, controlCode }) {
  const [config, setConfig] = useState({
    enabled: false,
    review_required_for: ['deployment', 'data_changes'],
  });

  return (
    <div>
      {/* Oversight Dashboard */}
      <HumanOversightDashboard config={config} projectId={projectId} />

      {/* Generic Config Panel - usePlugin={false} prevents infinite loop */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_08_Plugin;
